import React, { useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { MemberWithProfile, RoleWithId } from "../../services/TeamService";
import { TableRow, TableCell } from "../../design/table";
import { Badge } from "../../design/badge";
import { Button } from "../../design/button";
import { Text } from "../../design/text";
import { Avatar } from "../../design/avatar";
import { RoleSelect } from "./RoleSelect";
import type { MemberStatus } from "@visitproof/types";

interface MemberRowProps {
  member: MemberWithProfile;
  roles: RoleWithId[];
  canManageRoles: boolean;
  isCurrentUser: boolean;
  accountId: string;
  onStatusChange: (memberId: string, status: MemberStatus) => Promise<void>;
}

export const MemberRow: React.FC<MemberRowProps> = ({
  member,
  roles,
  canManageRoles,
  isCurrentUser,
  accountId,
  onStatusChange,
}) => {
  const [saving, setSaving] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixMsg, setFixMsg] = useState<string | null>(null);

  const handleFixAccess = async () => {
    setFixing(true);
    setFixMsg(null);
    try {
      const fn = httpsCallable(functions, "teamFixMemberAccess");
      await fn({ accountId, targetUid: member.id });
      setFixMsg("Fixed — ask them to sign out and back in.");
    } catch (error: any) {
      setFixMsg(error?.message || "Couldn't fix access.");
    } finally {
      setFixing(false);
    }
  };

  const displayName =
    member.profile?.firstName && member.profile?.lastName
      ? `${member.profile.firstName} ${member.profile.lastName}`
      : member.profile?.email || member.id;

  const initials =
    member.profile?.firstName && member.profile?.lastName
      ? `${member.profile.firstName[0]}${member.profile.lastName[0]}`
      : "?";

  const handleStatusToggle = async () => {
    setSaving(true);
    try {
      const newStatus: MemberStatus = member.status === "active" ? "suspended" : "active";
      await onStatusChange(member.id, newStatus);
    } catch (error) {
      console.error("Failed to toggle status:", error);
    } finally {
      setSaving(false);
    }
  };

  const memberTypeColor =
    member.memberType === "staff" ? "blue" : "green";

  const statusColor =
    member.status === "active"
      ? "green"
      : member.status === "suspended"
      ? "red"
      : "yellow";

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar
            src={member.profile?.avatarUrl}
            initials={initials}
            className="size-8"
          />
          <div>
            <Text className="font-medium">{displayName}</Text>
            {member.profile?.email && (
              <Text className="text-sm text-zinc-500">
                {member.profile.email}
              </Text>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell>
        <Badge color={memberTypeColor}>{member.memberType}</Badge>
      </TableCell>

      <TableCell>
        {member.roleIds.map((roleId) => {
          const role = roles.find((r) => r.id === roleId);
          return (
            <Badge key={roleId} color="zinc">
              {role?.name || roleId}
            </Badge>
          );
        })}
      </TableCell>

      <TableCell>
        <Badge color={statusColor}>{member.status}</Badge>
      </TableCell>

      {canManageRoles && (
        <TableCell>
          {!isCurrentUser && (
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-2">
                <Button
                  color={member.status === "active" ? "red" : "green"}
                  onClick={handleStatusToggle}
                  disabled={saving}
                >
                  {member.status === "active" ? "Suspend" : "Activate"}
                </Button>
                <Button outline onClick={handleFixAccess} disabled={fixing} title="Rebuild this person's access if they can't see or open assigned visits">
                  {fixing ? "Fixing…" : "Fix access"}
                </Button>
              </div>
              {fixMsg && <Text className="text-xs text-zinc-500">{fixMsg}</Text>}
            </div>
          )}
          {isCurrentUser && (
            <Text className="text-sm text-zinc-500">Current user</Text>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};
